import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCustomers, Customer } from "@/contexts/CustomerContext";
import { apiRequest } from "@/lib/api";
import { ArrowLeft, Edit2, Save, Trash2, Phone, Mail, MessageSquare, Sparkles, Image, X, Zap, Send, Loader2, Check, Copy } from "lucide-react";
import "./CustomerDetail.css";

const STATUS_OPTIONS: Customer["status"][] = ["new", "active", "hot_lead", "stalled", "closed"];

interface FollowUpTask {
  type: string;
  title: string;
  urgency: "high" | "medium" | "low";
  message: string;
  reason: string;
}

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function urgencyColor(u: string) {
  return u === "high" ? "#EF4444" : u === "medium" ? "#F59E0B" : "#10B981";
}

function urgencyBg(u: string) {
  return u === "high" ? "#FEF2F2" : u === "medium" ? "#FFFBEB" : "#ECFDF5";
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCustomer, updateCustomer, deleteCustomer } = useCustomers();
  const customer = getCustomer(id || "");
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Customer>>({});
  const [followUps, setFollowUps] = useState<FollowUpTask[]>([]);
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);
  const [followUpError, setFollowUpError] = useState("");
  const [sentMessages, setSentMessages] = useState<Set<number>>(new Set());
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  if (!customer) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Customer not found</h3>
          <button className="btn btn-primary" onClick={() => navigate("/customers")}>Back to Customers</button>
        </div>
      </div>
    );
  }

  const startEdit = () => {
    setEditData({ name: customer.name, phone: customer.phone, email: customer.email, status: customer.status, budget: customer.budget, needs: customer.needs, notes: customer.notes });
    setIsEditing(true);
  };

  const saveEdit = () => {
    updateCustomer(customer.id, editData);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (confirm(`Delete ${customer.name}?`)) {
      deleteCustomer(customer.id);
      navigate("/customers");
    }
  };

  const generateFollowUp = async () => {
    setIsGeneratingFollowUp(true);
    setFollowUpError("");
    try {
      const data = await apiRequest<{ tasks: FollowUpTask[] }>("/followups/generate-single", {
        method: "POST",
        body: JSON.stringify({ customer }),
      });
      setFollowUps(data.tasks || []);
      setSentMessages(new Set());
    } catch (err: any) {
      setFollowUpError(err.message || "Failed to generate follow-ups");
    }
    setIsGeneratingFollowUp(false);
  };

  const sendSms = (idx: number, task: FollowUpTask) => {
    const body = encodeURIComponent(task.message);
    window.open(`sms:${customer.phone}?body=${body}`, "_blank");
    setSentMessages((prev) => new Set(prev).add(idx));
  };

  const copyMessage = (idx: number, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const val = (field: keyof Customer) => (isEditing ? (editData[field] as string) ?? customer[field] : customer[field]);

  return (
    <div className="page">
      <div className="detail-header">
        <button className="btn btn-secondary" onClick={() => navigate("/customers")}>
          <ArrowLeft size={16} /> Back
        </button>
        <div className="detail-actions">
          {isEditing ? (
            <button className="btn btn-primary" onClick={saveEdit}><Save size={16} /> Save</button>
          ) : (
            <button className="btn btn-secondary" onClick={startEdit}><Edit2 size={16} /> Edit</button>
          )}
          <button className="btn btn-danger" onClick={handleDelete}><Trash2 size={16} /> Delete</button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="card detail-profile">
          <div className="profile-top">
            <div className="avatar-lg">{customer.name.split(" ").map((n) => n[0]).join("")}</div>
            {isEditing ? (
              <input className="edit-name" value={val("name")} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
            ) : (
              <h2>{customer.name}</h2>
            )}
            {isEditing ? (
              <div className="status-picker">
                {STATUS_OPTIONS.map((s) => (
                  <button key={s} className={`filter-chip ${editData.status === s ? "active" : ""}`}
                    onClick={() => setEditData({ ...editData, status: s })}>
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            ) : (
              <span className={`status-badge status-${customer.status}`}>{customer.status.replace("_", " ")}</span>
            )}
          </div>
          <div className="contact-actions">
            <a href={`tel:${customer.phone}`} className="contact-action-btn">
              <Phone size={16} /> Call
            </a>
            <a href={`sms:${customer.phone}`} className="contact-action-btn">
              <MessageSquare size={16} /> Text
            </a>
            <a href={`mailto:${customer.email}`} className="contact-action-btn">
              <Mail size={16} /> Email
            </a>
            <button className="contact-action-btn accent" onClick={() => navigate("/fred")}>
              <Sparkles size={16} /> Ask Fred
            </button>
          </div>
        </div>

        <div className="detail-info">
          <div className="card">
            <h3>Contact Information</h3>
            <div className="info-grid">
              <div className="form-group">
                <label>Phone</label>
                {isEditing ? <input value={val("phone")} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
                  : <p>{customer.phone}</p>}
              </div>
              <div className="form-group">
                <label>Email</label>
                {isEditing ? <input value={val("email")} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                  : <p>{customer.email}</p>}
              </div>
              <div className="form-group">
                <label>Budget</label>
                {isEditing ? <input value={val("budget")} onChange={(e) => setEditData({ ...editData, budget: e.target.value })} />
                  : <p>{customer.budget}</p>}
              </div>
            </div>
          </div>

          <div className="card followup-section">
            <div className="followup-header">
              <h3><Zap size={16} /> AI Follow-Up Autopilot</h3>
              <button className="btn btn-primary btn-sm" onClick={generateFollowUp} disabled={isGeneratingFollowUp}>
                {isGeneratingFollowUp ? <><Loader2 size={14} className="spinning" /> Generating...</> : <><Sparkles size={14} /> Generate Follow-Ups</>}
              </button>
            </div>

            {followUpError && <div className="followup-error">{followUpError}</div>}

            {followUps.length > 0 ? (
              <div className="followup-cards">
                {followUps.map((task, i) => (
                  <div key={i} className="followup-card" style={{ borderLeftColor: urgencyColor(task.urgency) }}>
                    <div className="followup-card-top">
                      <div className="followup-card-title">
                        <span className="followup-urgency-dot" style={{ background: urgencyColor(task.urgency) }} />
                        <strong>{task.title}</strong>
                      </div>
                      <span className="followup-type-badge" style={{ background: urgencyBg(task.urgency || "low"), color: urgencyColor(task.urgency || "low") }}>
                        {(task.type || "follow up").replace("_", " ")}
                      </span>
                    </div>
                    <p className="followup-reason">{task.reason}</p>
                    <div className="followup-message-box">
                      <MessageSquare size={14} />
                      <p>{task.message}</p>
                    </div>
                    <div className="followup-card-actions">
                      {sentMessages.has(i) ? (
                        <span className="followup-sent-badge"><Check size={14} /> Sent</span>
                      ) : (
                        <button className="btn btn-success btn-sm" onClick={() => sendSms(i, task)}>
                          <Send size={14} /> Send SMS
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => copyMessage(i, task.message)}>
                        {copiedIdx === i ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : !isGeneratingFollowUp ? (
              <div className="followup-empty">
                <Zap size={24} />
                <p>Generate AI-powered follow-up messages tailored to {customer.name}'s profile and needs</p>
              </div>
            ) : null}
          </div>

          <div className="card">
            <h3>Needs</h3>
            {isEditing ? <textarea value={val("needs")} onChange={(e) => setEditData({ ...editData, needs: e.target.value })} />
              : <div className="needs-tags">
                {customer.needs.split(",").map((n, i) => <span key={i} className="need-tag">{n.trim()}</span>)}
              </div>}
          </div>

          <div className="card">
            <h3>Notes</h3>
            {isEditing ? <textarea value={val("notes")} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={4} />
              : <p className="notes-text">{customer.notes || "No notes"}</p>}
          </div>

          <div className="card pinned-images-section">
            <h3><Image size={16} /> Pinned Room Visuals</h3>
            {customer.pinnedImages && customer.pinnedImages.length > 0 ? (
              <div className="pinned-images-grid">
                {customer.pinnedImages.map((pin, i) => (
                  <div key={i} className="pinned-image-item">
                    <img src={pin.url} alt={pin.label} />
                    <div className="pinned-image-info">
                      <span className="pinned-image-label" title={pin.label}>{pin.label}</span>
                      <button className="pinned-image-remove" title="Remove" onClick={() => {
                        const updated = (customer.pinnedImages || []).filter((_, idx) => idx !== i);
                        updateCustomer(customer.id, { pinnedImages: updated });
                      }}>
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="pinned-empty">No pinned room visuals yet. Generate visuals in Room Rebuild and pin them here.</p>
            )}
          </div>

          <div className="card">
            <h3>Activity</h3>
            <div className="activity-items">
              <div className="activity-item"><span className="activity-label">Last contact</span><span>{timeAgo(customer.lastContact)}</span></div>
              <div className="activity-item"><span className="activity-label">Created</span><span>{new Date(customer.createdAt).toLocaleDateString()}</span></div>
              <div className="activity-item"><span className="activity-label">Assigned to</span><span>{customer.assignedTo}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
